import { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

// Inject a <style> tag for dark mode support since this component
// intentionally avoids Tailwind (must render even if CSS fails to load).
const darkModeStyles = `
  @media (prefers-color-scheme: dark) {
    .geb-root { background-color: #0f172a !important; }
    .geb-title { color: #f1f5f9 !important; }
    .geb-desc { color: #94a3b8 !important; }
    .geb-details { background: #1e293b !important; }
    .geb-summary { color: #cbd5e1 !important; }
    .geb-pre { color: #94a3b8 !important; }
  }
`;

export class GlobalErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('GlobalErrorBoundary caught an error:', error, errorInfo);
  }

  handleClearCacheAndReload = async () => {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <>
          <style dangerouslySetInnerHTML={{ __html: darkModeStyles }} />
          <div className="geb-root" style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
            textAlign: 'center',
            fontFamily: 'system-ui, sans-serif',
            backgroundColor: '#f8fafc'
          }}>
            <h1 className="geb-title" style={{ fontSize: '24px', marginBottom: '16px', color: '#1e293b' }}>
              Something went wrong
            </h1>
            <p className="geb-desc" style={{ color: '#64748b', marginBottom: '24px', maxWidth: '400px' }}>
              The app couldn't load properly. This usually fixes itself with a refresh.
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <button 
                onClick={() => window.location.reload()}
                style={{ 
                  padding: '12px 24px', 
                  background: '#1e40af', 
                  color: 'white', 
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Reload Page
              </button>
              <button 
                onClick={this.handleClearCacheAndReload}
                style={{ 
                  padding: '12px 24px', 
                  background: '#dc2626', 
                  color: 'white', 
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Clear Cache & Reload
              </button>
            </div>
            {this.state.error && (
              <details className="geb-details" style={{ 
                marginTop: '24px', 
                padding: '16px', 
                background: '#f1f5f9', 
                borderRadius: '8px',
                maxWidth: '500px',
                textAlign: 'left'
              }}>
                <summary className="geb-summary" style={{ cursor: 'pointer', fontWeight: '500', color: '#475569' }}>
                  Error Details
                </summary>
                <pre className="geb-pre" style={{ 
                  marginTop: '12px', 
                  fontSize: '12px', 
                  overflow: 'auto',
                  color: '#64748b'
                }}>
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </>
      );
    }
    return this.props.children;
  }
}
